import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { tap, shareReplay } from 'rxjs/operators';

export interface Web3Config {
  bscChainId: number;
  usdtContractAddress: string;
  usdcContractAddress?: string;
  requiredConfirmations: number;
  pollInterval: number;
}

export interface ReownConfig {
  projectId: string;
  metadata: {
    name: string;
    description: string;
    url: string;
    icons: string[];
  };
}

export interface AppConfig {
  web3: Web3Config;
  reown: ReownConfig;
}

export type PaymentMethodType = 'ewallet' | 'web3_payment' | 'credit_card' | 'paypal';

export interface PaymentMethodsConfig {
  enabled_methods: PaymentMethodType[];
  available_methods: PaymentMethodType[];
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private configCache: AppConfig | null = null;
  private config$ = new BehaviorSubject<AppConfig | null>(null);
  private paymentMethodsCache: PaymentMethodsConfig | null = null;
  private paymentMethods$ = new BehaviorSubject<PaymentMethodsConfig | null>(null);

  constructor(private http: HttpClient) {}

  /**
   * Fetch Web3 and Reown configuration from backend API
   * Uses caching to avoid multiple requests
   */
  getConfig(): Observable<AppConfig> {
    if (this.configCache) {
      return of(this.configCache);
    }

    return this.http.get<AppConfig>('/auth/web3_config').pipe(
      tap(config => {
        this.configCache = config;
        this.config$.next(config);
      }),
      shareReplay(1)
    );
  }

  /**
   * Get cached configuration synchronously
   * Returns null if config hasn't been loaded yet
   */
  getCachedConfig(): AppConfig | null {
    return this.configCache;
  }

  /**
   * Observable stream of configuration changes
   */
  get config$Observable(): Observable<AppConfig | null> {
    return this.config$.asObservable();
  }

  /**
   * Fetch payment methods configuration from backend API
   * Uses caching to avoid multiple requests
   */
  getPaymentMethods(): Observable<PaymentMethodsConfig> {
    if (this.paymentMethodsCache) {
      return of(this.paymentMethodsCache);
    }

    return this.http.get<PaymentMethodsConfig>('/api/config/payment_methods').pipe(
      tap(config => {
        this.paymentMethodsCache = config;
        this.paymentMethods$.next(config);
      }),
      shareReplay(1)
    );
  }

  /**
   * Get cached payment methods configuration synchronously
   * Returns null if config hasn't been loaded yet
   */
  getCachedPaymentMethods(): PaymentMethodsConfig | null {
    return this.paymentMethodsCache;
  }

  /**
   * Check if a specific payment method is enabled
   * Returns false if config hasn't been loaded yet
   */
  isPaymentMethodEnabled(method: PaymentMethodType): boolean {
    return this.paymentMethodsCache?.enabled_methods.includes(method) ?? false;
  }

  /**
   * Observable stream of payment methods configuration changes
   */
  get paymentMethods$Observable(): Observable<PaymentMethodsConfig | null> {
    return this.paymentMethods$.asObservable();
  }

  /**
   * Clear cached configuration (useful for testing or refreshing)
   */
  clearCache(): void {
    this.configCache = null;
    this.config$.next(null);
    this.paymentMethodsCache = null;
    this.paymentMethods$.next(null);
  }
}
